<template>
  <div class="d-flex flex-column" style="gap: 0.5rem">
    <div class="acknowledgement-consent" ref="container">
      <div ref="agreementData" @scroll="onScroll">
        <p>Flash Money Pty Ltd, A.B.N 71 619 737 656, Australian Credit License 508661. I understand that by completing
          an application form that Flash Money Pty Ltd (the entity) will collect, use, hold and disclose my information
          as outlined in the Privacy Policy, which I declare that I have read and understood.</p>

        <h5>Collection of information</h5>
        <p>
          I consent to the entity collecting personal and credit information from me. I understand that if I do not
          provide all information that is requested from me the entity may be unable to provide me with their financial
          services. I also understand that if any of my personal information should change, such as residential address,
          phone number, email address, etc., it is my responsibility to advise the entity of this. I understand it may
          not always be practical or reasonable to collect information from me and as such, I provide my consent for the
          entity to collect personal information about me on an ongoing basis from:
        </p>
        <ol type="i">
          <li>publicly available sources such as public registers;</li>
          <li>credit reporting bodies;</li>
          <li>employers or income providers;</li>
          <li>other financial service providers and financial institutions;</li>
        </ol>
        <p>If any of these parties should request this Acknowledgement and Consent before providing information, I
          consent for this to be provided to them.</p>

        <h5>Use of information</h5>
        <p>I consent to the entity collecting my information for the purposes of providing me with financial services
          and any related purpose including but not limited to:

          lending activities including accepting and assessing loan applications and disbursement of credit;

          credit management functions relating to the management of accounts and collection of overdue amounts; customer
          service requests such as providing access to and updating personal information and resolving disputes;
          gathering information and statistical data to analyze and evaluate business functions;

          developing our service offerings and improving overall business processes and efficiency.
        </p>
        <h5>Disclosure of information</h5>
        <p>I consent to the entity disclosing my personal information to provide me with their financial services and
          any other related purpose as outlined above, on an ongoing basis to:</p>
        <ol type="i">
          <li>my personal point of contacts,</li>
          <li>my employers and income providers;</li>
          <li>other financial institutions and debt collection agencies;</li>
          <li>credit providers, credit brokers, other referrers and other service providers. Financiers, insurers,
            auditors, contractors, agents or service providers the entity uses to offer services;
          </li>
          <li>any other entity.</li>
        </ol>
        <p>I understand my information may also be disclosed in other circumstances outlined in the entity's privacy
          policy.</p>
        <p>I understand the entity may, in the ordinary course of our business, disclose my credit information to third
          parties which operate in the United States of America and while these parties may not, therefore, be subject
          to Australian Privacy Law, no person in those countries will be able to access my credit eligibility
          information without my express consent.</p>
        <h5>Promotion of Products and Services</h5>
        <p>I consent to being contacted by the entity, and any external third parties or agents who act on their behalf,
          for the purposes of the promotion of products and services. I understand the entity may promote the products
          of their marketing partners. I understand that I may withdraw my consent and request not to receive
          promotional communications at any time by contacting the entity.</p>
      </div>

      <div ref="scrollTarget">
        <TransitionGroup class="d-block" mode="out-in" name="slide-fade" tag="div">
          <div v-if="!done" key="see-more"
               class="d-flex justify-content-center align-items-center p-3 see-more"
               @click="scrollBy">
            <span>See More</span>
            <svg class="bi bi-arrow-down-short" fill="currentColor" height="24" viewBox="0 0 16 16"
                 width="24" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M8 4a.5.5 0 0 1 .5.5v5.793l2.146-2.147a.5.5 0 0 1 .708.708l-3 3a.5.5 0 0 1-.708 0l-3-3a.5.5 0 1 1 .708-.708L7.5 10.293V4.5A.5.5 0 0 1 8 4z"
                fill-rule="evenodd"/>
            </svg>
          </div>

          <Checkbox v-else key="acknowledgement-checkbox" v-model="proxyValue"
                    class="m-0 d-flex align-items-center acknowledgement-checkbox" style="width: 100%">
            <template v-slot="{ onClick, style }">
              <div :style="style" @click="onClick">
                <div class="d-flex flex-column" style="gap: 0.5rem">
                  <strong style="cursor: pointer">I have read, understood and agree with Flash Money's
                    Acknowledgement and Consent.</strong>
                  <a href="/docs/Acknowledgement and Consent.pdf" target="_blank" @click.stop>Get a copy of consent
                    document.</a>
                </div>
              </div>
            </template>
          </Checkbox>
        </TransitionGroup>
      </div>
    </div>

    <small class="ac-hint">
      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
      </svg>
      Read to the bottom to enable acknowledgement.
    </small>
  </div>
</template>

<script>
import Checkbox from '@modules/shared/components/form/FmCheckbox.vue';
import {scrollToElement} from '@modules/shared/helpers/form';

export default {
  name: 'AcknowledgementAndConsent',
  props: ['value'],
  components: {Checkbox},
  data() {
    return {
      done: false,
      initialized: false,
    };
  },
  computed: {
    proxyValue: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
  },
  methods: {
    onScroll() {
      if (!this.initialized) {
        return;
      }

      /**
       * @see https://thewebdev.info/2021/02/12/how-to-detect-a-mobile-device-with-javascript/
       * @type {boolean}
       */
      const isMobile = ('ontouchstart' in document.documentElement && !!navigator.userAgent.match(/Mobi/));

      if (isMobile) {
        scrollToElement(this.$refs.container, 'smooth', true);
      }

      if (this.done) {
        return;
      }

      const {offsetHeight, scrollTop, scrollHeight} = this.$refs.agreementData;
      const scrollPercentage = (offsetHeight + scrollTop) / scrollHeight;

      if (scrollPercentage >= 0.99) {
        this.done = true;
      }
    },
    scrollBy() {
      const {offsetHeight} = this.$refs.agreementData;
      this.$refs.agreementData.scrollBy({top: Math.ceil(offsetHeight * 0.85), behavior: 'smooth'});
    },
  },
  mounted() {
    this.$refs.agreementData.scrollTo({top: 0});
    this.initialized = true;
    // If the user previously agreed (draft restore / page refresh), show the
    // checkbox directly — don't force another scroll-through.
    this.done = !!this.value;
  },
};
</script>

<style lang="scss">
$ac-amber:     #FFB000;
$ac-ink:       #15171C;
$ac-ink-soft:  #5B606B;
$ac-ink-faint: #9197A1;
$ac-line:      #E7E4DA;
$ac-surface:   #FBFAF6;

// ── Container ────────────────────────────────────────────────────────────────
.acknowledgement-consent {
  position: relative;
  background: #fff;
  border: 1.5px solid $ac-line;
  border-radius: 14px;
  overflow: hidden;

  // ── Scrollable text area ─────────────────────────────────────────────────
  > div:first-child {
    padding: 1.25rem 1.5rem;
    max-height: 30rem;
    overflow-y: auto;
    font-size: 13.5px;
    line-height: 1.75;
    color: $ac-ink-soft;

    // Slim scrollbar
    scrollbar-width: thin;
    scrollbar-color: $ac-line transparent;
    &::-webkit-scrollbar       { width: 4px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb { background: $ac-line; border-radius: 4px; }

    p  { margin-bottom: 0.75rem; }
    ol { padding-left: 1.5rem; margin-bottom: 0.75rem;
      li { margin-bottom: 0.25rem; }
    }
  }

  // ── Section headings ─────────────────────────────────────────────────────
  h4, h5 {
    font-size: 10.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: $ac-ink;
    margin-top: 1.25rem;
    margin-bottom: 0.5rem;
  }

  // ── "See More" scroll CTA ────────────────────────────────────────────────
  .see-more {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-height: 52px;
    border-top: 1.5px solid $ac-line;
    background: $ac-surface;
    cursor: pointer;
    color: $ac-amber;
    font-weight: 600;
    font-size: 13.5px;
    transition: background 0.15s ease;
    user-select: none;

    &:hover { background: rgba(255, 176, 0, 0.06); }

    svg {
      animation: ac-bounce 1.8s ease-in-out infinite;
      color: $ac-amber;
    }
  }

  // ── Acknowledgement checkbox area ────────────────────────────────────────
  .acknowledgement-checkbox {
    border-top: 1.5px solid rgba(255, 176, 0, 0.3);

    > .check-box {
      min-height: 52px;
      background: rgba(255, 176, 0, 0.06);
      padding: 1rem 1.25rem;
      width: 100%;
    }

    .fm-checkbox {
      margin-bottom: 0 !important;
    }
  }
}

// ── Hint below the card ───────────────────────────────────────────────────────
.ac-hint {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: $ac-ink-faint;

  svg {
    flex-shrink: 0;
    opacity: 0.7;
  }
}

// ── Bounce animation for the "See More" arrow ─────────────────────────────────
@keyframes ac-bounce {
  0%, 100% { transform: translateY(0); }
  50%       { transform: translateY(5px); }
}

// ── Slide-fade transition (Vue TransitionGroup) ───────────────────────────────
.slide-fade-enter-active  { transition: all 0.2s ease-out; }
.slide-fade-leave-active  { transition: all 0.15s ease-in; }
.slide-fade-enter         { opacity: 0; transform: translateY(-6px); }
.slide-fade-leave-to      { opacity: 0; transform: translateY(6px); }
</style>
